import { useParams } from "react-router";

import type { RoutePath } from "app/constants/paths";
import type { ParamParseKey } from "react-router";

/**
 * `useParams`, typed by the registry pattern(s) of the route(s) that render
 * the calling component, so param names are checked against
 * `app/constants/paths` instead of being self-asserted:
 *
 * ```ts
 * const { groupUuid } = useRouteParams(paths.groupDetails);
 * ```
 *
 * A component rendered under several routes passes all of them and gets the
 * union of their params:
 *
 * ```ts
 * const { key, sectionKey } = useRouteParams(
 *   paths.dashboard,
 *   paths.dashboardSectionDetails,
 * );
 * ```
 *
 * Values are `string | undefined` — the type cannot narrow to the single
 * matched route, so destructure with a default or handle `undefined`.
 */
export function useRouteParams<Path extends RoutePath>(
  ...patterns: [Path, ...Path[]]
) {
  return useParams<ParamParseKey<(typeof patterns)[number]>>();
}
